import { Autocomplete, Icon, MenuItem, TextField } from '@mui/material';
import type React from 'react';
import { fieldSettings } from '../../environment';
import { useTranslation } from 'react-i18next';

interface ISelectFieldProps {
	id: string;
	label: string;
	placeholder?: string;
	obj?: any;
	value: any;
	valueList: any[];
	setValue: (value: any) => void;
	valueKey?: string;
	labelKey?: string;
	disabled?: boolean | false;
	readOnly?: boolean | false;
	autoFocus?: boolean | false;
	required?: boolean | false;
	hasNoneSelect?: boolean | false;
	iconName?: string;
	searchable?: boolean | false;
	indexKey?: number;
	indexValue?: number;
}

export const SelectField: React.FC<ISelectFieldProps> = ({
    id,
    label,
    placeholder,
    obj,
    value,
    valueList,
    setValue,
    valueKey = '',
    labelKey = '',
    disabled,
    autoFocus,
    required,
    readOnly,
    hasNoneSelect,
    iconName,
    searchable,
}) => {
    const { t } = useTranslation();
    const safeValue = value ?? '';
  
    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const newValue = event.target.value;
        setValue(obj ? { ...obj, [id]: newValue } : newValue);
    };

    if (searchable) {
        return (
            <Autocomplete
                // disablePortal
                id={id}
                options={valueList}
                getOptionLabel={(option) => option[labelKey] || ''}
                onChange={(_event, newValue) => {
                    setValue(
                        obj
                            ? { ...obj, [id]: newValue ? newValue[valueKey] : '' }
                            : newValue
                                ? newValue[valueKey]
                                : '',
                    );
                }}
                value={valueList.find((option) => option[valueKey] === safeValue) || null}
                renderOption={(props, option) => (
                    <MenuItem {...props}>
                        {iconName && <Icon sx={{ fontSize: 13, mr: 1 }}>{iconName}</Icon>}
                        {option[labelKey]}
                    </MenuItem>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder={placeholder || 'Select one'}
                        variant={fieldSettings.fieldVariant}
                        fullWidth
                        autoFocus={autoFocus}
                        disabled={disabled}
                        required={required}
                        // inputProps={{ readOnly: readOnly }} resolve error
                        size="small"
                    />
                )}
                isOptionEqualToValue={(option, value) => option[valueKey] === value[valueKey]}
                fullWidth
            />
        );
    }
    return (
        <TextField
            fullWidth
            value={safeValue}
            onChange={handleChange}
            size="small"
            id={id}
            label={label}
            placeholder={placeholder}
            select
            variant={fieldSettings.fieldVariant}
            autoFocus={autoFocus}
            disabled={disabled}
            required={required}
            inputProps={{ readOnly: readOnly }}
        >
            {hasNoneSelect && (
                <MenuItem value=''>
                    <em>{ t('components.selectField.none') }</em>
                </MenuItem>
            )}
            {valueList.map((option, index) => {
                const keyValue = valueKey
                    ? option[valueKey]
                    : Object.values<any>(option)[0];
                const displayValue = labelKey
                    ? option[labelKey]
                    : Object.values<any>(option)[1];
                return (
                    <MenuItem
                        key={index}
                        value={keyValue ? keyValue.valueOf() : ''}
                        sx={{ fontSize: 14 }}
                    >
                        {iconName && <Icon sx={{ fontSize: 13 }}>{iconName}</Icon>}
                        {displayValue ? displayValue.valueOf() : 'Undefined'}
                    </MenuItem>
                );
            })}
        </TextField>
    );
};
